import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useTheme, styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Snackbar from '@mui/material/Snackbar';
import Box from '@mui/material/Box';

import { Typography } from '@tkww/the-bash-frontend';

import { HeroWithConfetti } from 'components/Hero';
import SectionContainer from 'components/grid/SectionContainer';

import LearnMoreCtaSection from 'components/LearnMoreCtaSection';
import Select from 'components/Select';
import jump from 'helpers/jump';
import { ctaClickEvent, trackEvent } from 'state/modules/analytics';

import FaqGroup from 'views/HelpPage/FaqGroup';
import { FAQ_VIEWED, LEARN_MORE } from 'analytics/constants';

const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey[400]}`,
  marginBottom: 25,
  display: 'none',
  [theme.breakpoints.up('lg')]: {
    display: 'flex',
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'inherit',
  color: theme.palette.text.primary,
  fontFamily: theme.fonts.regular,
  fontSize: 16,
  lineHeight: 1.25,
  letterSpacing: 'normal',
  opacity: 1,
  minHeight: 62,
}));

const StyledSectionContainer = styled(SectionContainer)(() => ({
  paddingBottom: 1,
}));

const StyledFaqCategoryContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isVisible',
})(({ isVisible }) => ({
  display: isVisible ? 'block' : 'none',
}));

const dispatchFaqViewedEvent = (dispatch, category, questionGroup, question) => {
  dispatch(
    trackEvent(FAQ_VIEWED, {
      clickTarget: LEARN_MORE,
      faqEntry: question,
      faqGroup: questionGroup,
      faqCategory: category,
    })
  );
};

const HelpPage = ({ faqCategories, defaultCategory, defaultQuestion, defaultGroup }) => {
  const dispatch = useDispatch();
  useEffect(() => {
    if (defaultQuestion) {
      jump(`#question-${defaultQuestion}`);
      const foundCategory = faqCategories.find((category) => category.slug === defaultCategory);
      let foundFaqQuestion = null;
      const foundFaqGroup = foundCategory.faqGroups.find((faqGroup) => {
        const questionResult = faqGroup.faqEntries.find((entry) => entry.slug === defaultQuestion);
        if (questionResult) {
          foundFaqQuestion = questionResult;
        }
        return !!questionResult;
      });
      dispatchFaqViewedEvent(
        dispatch,
        foundCategory.category,
        foundFaqGroup.group,
        foundFaqQuestion.question
      );
    } else if (defaultGroup) {
      jump(`#group-${defaultGroup}`);
    }
  }, [defaultQuestion, defaultGroup]);

  const [selectedTab, setSelectedTab] = useState(defaultCategory);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const muiTheme = useTheme();

  const onLearnMoreClick = () => {
    dispatch(ctaClickEvent('Vendor Section', 'Learn More'));
    window.location.assign('https://itg.thebash.com');
  };

  const onCopy = () => {
    setSnackbarOpen(true);
  };

  const onCategorySelect = (categorySlug) => {
    setSelectedTab(categorySlug);
    const url = new URL(window.location);
    url.searchParams.set('category', categorySlug);
    url.searchParams.delete('question');
    url.searchParams.delete('group');
    window.history.pushState(null, '', url.toString());
  };

  const onAccordionChange = (category, questionGroup, question) => (event, newExpanded) => {
    if (newExpanded) {
      dispatchFaqViewedEvent(dispatch, category, questionGroup, question);
    }
  };

  return (
    <>
      <HeroWithConfetti
        title="Frequently Asked Questions"
        subtitle="Find answers to top questions about The Bash."
      />
      <StyledSectionContainer backgroundColor={muiTheme.palette.grey[200]}>
        <StyledTabs value={selectedTab} onChange={(event, value) => onCategorySelect(value)}>
          {faqCategories.map((faqCategory) => (
            <StyledTab
              key={faqCategory.id}
              value={faqCategory.slug}
              label={faqCategory.category}
              icon={
                <img
                  src={faqCategory.icon.url}
                  alt={faqCategory.icon.description}
                  width={faqCategory.icon.width}
                  height={faqCategory.icon.height}
                />
              }
              iconPosition="start"
            />
          ))}
        </StyledTabs>
        <Box
          sx={(theme) => ({
            paddingTop: '24px',
            [theme.breakpoints.up('lg')]: {
              display: 'none',
            },
          })}
        >
          <Typography variant="h4" component="label" htmlFor="faq-category">
            Select Topic:
          </Typography>
          <Select
            id="faq-category"
            name="faq-category"
            native
            value={selectedTab}
            onChange={(e) => onCategorySelect(e.target.value)}
            options={faqCategories.map((faqCategory) => ({
              label: faqCategory.category,
              value: faqCategory.slug,
            }))}
            sx={(theme) => ({
              background: theme.palette.background.default,
            })}
            disablePlaceholder
          />
        </Box>
        {faqCategories.map((faqCategory) => {
          const { faqGroups, id } = faqCategory;
          return faqGroups.map((faqGroup) => (
            <StyledFaqCategoryContainer key={id} isVisible={selectedTab === faqCategory.slug}>
              <FaqGroup
                key={faqGroup.id}
                faqCategory={faqCategory.category}
                faqGroup={faqGroup}
                onCopy={onCopy}
                defaultQuestion={defaultQuestion}
                onAccordionChange={onAccordionChange}
              />
            </StyledFaqCategoryContainer>
          ));
        })}
      </StyledSectionContainer>
      <LearnMoreCtaSection
        subtitle="Find answers to frequently asked questions from vendors on our blog."
        title="Are you a vendor?"
        onLearnMoreClick={onLearnMoreClick}
      />
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        ContentProps={{
          'aria-describedby': 'message-id',
        }}
        message={<span id="message-id">Copied to clipboard</span>}
        action={[
          <IconButton
            key="close"
            aria-label="close"
            color="inherit"
            onClick={() => setSnackbarOpen(false)}
            size="large"
          >
            <CloseIcon />
          </IconButton>,
        ]}
      />
    </>
  );
};

HelpPage.propTypes = {
  faqCategories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  defaultCategory: PropTypes.string.isRequired,
  defaultQuestion: PropTypes.string,
  defaultGroup: PropTypes.string,
};

HelpPage.defaultProps = {
  defaultQuestion: null,
  defaultGroup: null,
};

export default HelpPage;
